"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { requirePasskeyUiMutationApproval } from "./passkeyMutationGate";

const btnCls = "w-full rounded-[var(--radius-sm)] border px-4 py-2.5 text-[13.5px] font-semibold transition-opacity disabled:opacity-60";
const btnStyle = { borderColor: "var(--border)", color: "var(--text)", background: "var(--surface)" } as const;

/** ログイン画面用：パスキーでログインするボタン。 */
export function PasskeyLoginButton() {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onClick = async () => {
    requirePasskeyUiMutationApproval();
    setPending(true);
    setError(null);
    try {
      const optionsRes = await fetch("/api/auth/passkey/authenticate-options");
      if (!optionsRes.ok) throw new Error("パスキー認証の開始に失敗しました。");
      const options = await optionsRes.json();
      const response = await startAuthentication({ optionsJSON: options });
      const verifyRes = await fetch("/api/auth/passkey/authenticate-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ response }),
      });
      const result = await verifyRes.json();
      if (!verifyRes.ok || !result.ok) throw new Error(result.error || "パスキーログインに失敗しました。");
      router.push("/");
      router.refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "パスキーログインに失敗しました。");
    } finally {
      setPending(false);
    }
  };

  return (
    <div>
      <button type="button" onClick={onClick} disabled={pending} className={btnCls} style={btnStyle}>
        {pending ? "認証中…" : "🔑 パスキーでログイン"}
      </button>
      {error && <p className="mt-1.5 text-[12px]" style={{ color: "var(--expense)" }}>{error}</p>}
    </div>
  );
}

/**
 * 新しいパスキーを登録するボタン。
 * `inviteToken` を渡すと、未ログイン状態から招待受諾＝パスキーのみでの新規参加として動作する。
 */
export function PasskeyRegisterButton({ inviteToken, onDone }: { inviteToken?: string; onDone?: () => void }) {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");

  const onClick = async () => {
    requirePasskeyUiMutationApproval();
    setPending(true);
    setError(null);
    try {
      const optionsUrl = inviteToken ? `/api/auth/passkey/register-options?invite=${encodeURIComponent(inviteToken)}` : "/api/auth/passkey/register-options";
      const optionsRes = await fetch(optionsUrl);
      if (!optionsRes.ok) throw new Error("パスキー登録の開始に失敗しました。");
      const options = await optionsRes.json();
      const response = await startRegistration({ optionsJSON: options });
      const verifyRes = await fetch("/api/auth/passkey/register-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ response, deviceLabel: label }),
      });
      const result = await verifyRes.json();
      if (!verifyRes.ok || !result.ok) throw new Error(result.error || "パスキー登録に失敗しました。");
      setLabel("");
      if (onDone) onDone();
      else {
        router.push("/");
        router.refresh();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "パスキー登録に失敗しました。");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="端末名（例：会社のノートPC）"
        className="rounded-[var(--radius-sm)] border px-3 py-2 text-[13px]"
        style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}
      />
      <button
        type="button"
        onClick={onClick}
        disabled={pending}
        className="rounded-[var(--radius-sm)] px-4 py-2 text-[13px] font-semibold disabled:opacity-60"
        style={{ background: "var(--accent)", color: "#ffffff" }}
      >
        {pending ? "登録中…" : "＋ このデバイスにパスキーを登録"}
      </button>
      {error && <p className="w-full text-[12px]" style={{ color: "var(--expense)" }}>{error}</p>}
    </div>
  );
}
